import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from '../store';

/** Chrome/Edge/Android `beforeinstallprompt` event (not in TS lib DOM). */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'pwa_install_dismissed_at';
const SNOOZE_MS = 1000 * 60 * 60 * 24 * 14; // re-offer after 14 days

const T = {
  he: {
    title: 'התקן את TraderYo',
    body: 'הוסף למסך הבית לגישה מהירה, מסך מלא ועבודה גם בלי רשת.',
    install: 'התקן',
    later: 'אחר כך',
    gotIt: 'הבנתי',
    iosBody: 'להתקנה: הקש על כפתור השיתוף',
    iosBody2: 'ואז «הוסף למסך הבית».',
  },
  en: {
    title: 'Install TraderYo',
    body: 'Add it to your home screen for quick access, full-screen, and offline use.',
    install: 'Install',
    later: 'Later',
    gotIt: 'Got it',
    iosBody: 'To install: tap the Share button',
    iosBody2: 'then “Add to Home Screen”.',
  },
};

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIos() {
  return (
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
    !(window as unknown as { MSStream?: unknown }).MSStream
  );
}

export default function InstallPrompt() {
  const { lang } = useStore() as { lang: 'he' | 'en' };
  const location = useLocation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);
  const t = T[lang] ?? T.en;
  const rtl = lang === 'he';

  // Only inside the app, never on marketing/auth pages.
  const inApp = location.pathname.startsWith('/dashboard');

  useEffect(() => {
    if (!inApp || isStandalone()) return;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < SNOOZE_MS) return;

    // Android / Chromium: capture the install event.
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS Safari never fires the event — show manual instructions instead.
    if (isIos()) {
      setIos(true);
      const timer = setTimeout(() => setShow(true), 2500);
      return () => {
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
        clearTimeout(timer);
      };
    }

    const onInstalled = () => setShow(false);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [inApp]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setShow(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!show) return null;

  return (
    <div
      dir={rtl ? 'rtl' : 'ltr'}
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        zIndex: 9000,
        maxWidth: 460,
        margin: '0 auto',
        background: 'var(--s1)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--rad-l)',
        boxShadow: '0 12px 40px rgba(10,18,48,0.18)',
        padding: 16,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: 12,
          background: 'var(--grad)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src="/favicon.png" alt="" width={28} height={28} style={{ borderRadius: 8 }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: 'var(--t1)', fontSize: 15, marginBottom: 3 }}>
          {t.title}
        </div>
        <div style={{ color: 'var(--t2)', fontSize: 13, lineHeight: 1.5 }}>
          {ios ? (
            <>
              {t.iosBody} <span style={{ fontWeight: 700 }}>⎙</span> {t.iosBody2}
            </>
          ) : (
            t.body
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {ios ? (
            <button
              onClick={dismiss}
              className="btn-ghost"
              style={{
                borderRadius: 'var(--rad)',
                padding: '8px 18px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t.gotIt}
            </button>
          ) : (
            <>
              <button
                onClick={install}
                style={{
                  background: 'var(--primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 'var(--rad)',
                  padding: '8px 18px',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {t.install}
              </button>
              <button
                onClick={dismiss}
                className="btn-ghost"
                style={{
                  borderRadius: 'var(--rad)',
                  padding: '8px 14px',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {t.later}
              </button>
            </>
          )}
        </div>
      </div>

      <button
        onClick={dismiss}
        aria-label="close"
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          color: 'var(--t3)',
          fontSize: 20,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 2,
        }}
      >
        ×
      </button>
    </div>
  );
}
